import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, Sun, Moon, User, LogOut, LayoutDashboard, Calendar, ScanLine, Ticket } from 'lucide-react';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return '/login';
    switch (user.role) {
      case 'admin': return '/admin';
      case 'organizer': return '/organizer';
      case 'staff': return '/scanner';
      default: return '/tickets';
    }
  };

  // Check if user should see "Mis Tickets" - only buyers
  const showMyTickets = user?.role === 'buyer';

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">MT</span>
            </div>
            <span className="text-lg font-bold tracking-tight hidden sm:block">
              Master<span className="text-primary">Ticket</span>HN.com
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/events" className="text-muted-foreground hover:text-foreground transition-colors font-medium" data-testid="nav-events">
              Eventos
            </Link>
            {/* Only show "Mis Tickets" for buyers */}
            {isAuthenticated && showMyTickets && (
              <Link to="/tickets" className="text-muted-foreground hover:text-foreground transition-colors font-medium" data-testid="nav-tickets">
                Mis Tickets
              </Link>
            )}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                        <User className="w-4 h-4 text-primary-foreground" />
                      </div>
                    )}
                    <span className="hidden sm:block font-medium">{user?.name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                    <p className="text-xs text-primary capitalize mt-1">{user?.role}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(getDashboardLink())} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    {user?.role === 'admin' ? 'Admin Panel' : user?.role === 'organizer' ? 'Mi Dashboard' : user?.role === 'staff' ? 'Scanner' : 'Mis Tickets'}
                  </DropdownMenuItem>
                  
                  {/* Only show Mis Tickets for buyers */}
                  {showMyTickets && (
                    <DropdownMenuItem onClick={() => navigate('/tickets')} data-testid="menu-tickets">
                      <Ticket className="w-4 h-4 mr-2" />
                      Mis Tickets
                    </DropdownMenuItem>
                  )}
                  
                  {user?.role === 'organizer' && (
                    <DropdownMenuItem onClick={() => navigate('/organizer/events/new')} data-testid="menu-create-event">
                      <Calendar className="w-4 h-4 mr-2" />
                      Crear Evento
                    </DropdownMenuItem>
                  )}
                  {(user?.role === 'staff' || user?.role === 'admin' || user?.role === 'organizer') && (
                    <DropdownMenuItem onClick={() => navigate('/scanner')} data-testid="menu-scanner">
                      <ScanLine className="w-4 h-4 mr-2" />
                      Escanear QR
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={() => navigate('/login')} className="font-medium" data-testid="login-button">
                  Ingresar
                </Button>
                <Button onClick={() => navigate('/register')} className="btn-primary rounded-lg" data-testid="register-button">
                  Registrarse
                </Button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border">
            <div className="flex flex-col gap-2">
              <Link
                to="/events"
                className="px-4 py-2 rounded-lg hover:bg-muted transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Eventos
              </Link>
              {/* Only show Mis Tickets for buyers in mobile menu */}
              {isAuthenticated && showMyTickets && (
                <Link
                  to="/tickets"
                  className="px-4 py-2 rounded-lg hover:bg-muted transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Mis Tickets
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
